import { useState, useEffect, useRef } from 'react';
import * as React from 'react';
import { FaToggleOn, FaToggleOff, FaPlus, FaTimes, FaSave, FaLink, FaEnvelope, FaChevronDown, FaChevronRight, FaUpload, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import FormConfigService from '../services/FormConfigService';
import type { ApplicationFormConfig, StandardFieldConfig, CustomFieldConfig, RecruitingEmailTemplate } from '../types';

export default function FormConfigEditor(): React.JSX.Element {
  const [config, setConfig] = useState<ApplicationFormConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeText, setWelcomeText] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [standardFields, setStandardFields] = useState<StandardFieldConfig[]>([]);
  const [customFields, setCustomFields] = useState<CustomFieldConfig[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<RecruitingEmailTemplate[]>([]);
  const [emailLogoUrl, setEmailLogoUrl] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await FormConfigService.getFormConfig();
      setConfig(data);
      setSlug(data.slug || '');
      setIsActive(data.is_active);
      setWelcomeTitle(data.welcome_title || '');
      setWelcomeText(data.welcome_text || '');
      setSuccessMessage(data.success_message || '');
      setStandardFields(data.standard_fields || []);
      setCustomFields(data.custom_fields || []);
      setEmailTemplates(data.email_templates || []);
      setEmailLogoUrl(data.email_logo_url || '');
      setEmailReplyTo(data.email_reply_to || '');
    } catch (e: any) {
      toast.error('Formular-Konfiguration konnte nicht geladen werden.');
      console.error('[FormConfigEditor] load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!slug.trim()) {
      toast.error('URL-Slug darf nicht leer sein.');
      return;
    }
    try {
      setSaving(true);
      const updated = await FormConfigService.updateFormConfig({
        slug,
        is_active: isActive,
        welcome_title: welcomeTitle,
        welcome_text: welcomeText,
        success_message: successMessage,
        email_templates: emailTemplates,
        email_reply_to: emailReplyTo,
        standard_fields: standardFields,
        custom_fields: customFields,
      });
      setConfig(updated);
      toast.success('Formular-Konfiguration gespeichert.');
    } catch (e: any) {
      toast.error('Speichern fehlgeschlagen.');
      console.error('[FormConfigEditor] save error:', e);
    } finally {
      setSaving(false);
    }
  };

  const toggleStandardField = (index: number, field: 'enabled' | 'required') => {
    setStandardFields(prev => prev.map((f, i) =>
      i === index ? { ...f, [field]: !f[field] } : f
    ));
  };

  const updateStandardFieldLabel = (index: number, label: string) => {
    setStandardFields(prev => prev.map((f, i) =>
      i === index ? { ...f, label } : f
    ));
  };

  const addCustomField = () => {
    setCustomFields(prev => [
      ...prev,
      { key: `custom_${Date.now()}`, label: '', type: 'text', required: false },
    ]);
  };

  const updateCustomField = (index: number, updates: Partial<CustomFieldConfig>) => {
    setCustomFields(prev => prev.map((f, i) =>
      i === index ? { ...f, ...updates } : f
    ));
  };

  const removeCustomField = (index: number) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  const updateEmailTemplate = (step: string, updates: Partial<RecruitingEmailTemplate>) => {
    setEmailTemplates(prev => prev.map(t =>
      t.step === step ? { ...t, ...updates } : t
    ));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilddateien erlaubt (PNG, JPG, etc.).');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Datei zu gross. Maximal 2MB erlaubt.');
      return;
    }

    try {
      setUploadingLogo(true);
      const url = await FormConfigService.uploadEmailLogo(file);
      setEmailLogoUrl(url);
      toast.success('Logo hochgeladen.');
    } catch (err: any) {
      toast.error('Logo-Upload fehlgeschlagen.');
      console.error('[FormConfigEditor] logo upload error:', err);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    try {
      setUploadingLogo(true);
      await FormConfigService.deleteEmailLogo();
      setEmailLogoUrl('');
      toast.success('Logo entfernt.');
    } catch (err: any) {
      toast.error('Logo konnte nicht entfernt werden.');
      console.error('[FormConfigEditor] logo delete error:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${colors.border}`, fontSize: 13, outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4,
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>Lade Konfiguration...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* General Settings */}
      <Card title="Allgemeine Einstellungen">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14 }}>
          <label style={{ fontSize: 13, color: colors.textBody, fontWeight: 500 }}>Formular aktiv</label>
          <button
            onClick={() => setIsActive(!isActive)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22 }}
          >
            {isActive
              ? <FaToggleOn color={colors.success} />
              : <FaToggleOff color={colors.textMuted} />
            }
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>URL-Slug</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={slug} onChange={e => setSlug(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="z.B. katchi-cream" />
            {slug && (
              <span style={{ fontSize: 11, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                <FaLink style={{ fontSize: 10 }} /> /jobs/{slug}
              </span>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Willkommenstitel</label>
          <input value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} style={inputStyle} placeholder="Werde Teil unseres Teams!" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Willkommenstext</label>
          <textarea value={welcomeText} onChange={e => setWelcomeText(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Wir suchen motivierte..." />
        </div>

        <div>
          <label style={labelStyle}>Erfolgsmeldung nach Bewerbung</label>
          <textarea value={successMessage} onChange={e => setSuccessMessage(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Danke für deine Bewerbung!" />
        </div>
      </Card>

      {/* Standard Fields */}
      <Card title="Standardfelder">
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 12px' }}>
          Aktiviere/deaktiviere Felder und passe Labels an. Pflichtfelder werden im Formular markiert.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {standardFields.map((field, i) => (
            <div
              key={field.key}
              style={{
                display: 'grid', gridTemplateColumns: '32px 1fr 80px 80px', gap: 8,
                alignItems: 'center', padding: '6px 8px', borderRadius: 8,
                background: field.enabled ? 'transparent' : colors.bgMuted,
                opacity: field.enabled ? 1 : 0.5,
              }}
            >
              <button
                onClick={() => toggleStandardField(i, 'enabled')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: 0 }}
                title={field.enabled ? 'Deaktivieren' : 'Aktivieren'}
              >
                {field.enabled
                  ? <FaToggleOn color={colors.success} />
                  : <FaToggleOff color={colors.textMuted} />
                }
              </button>

              <input
                value={field.label}
                onChange={e => updateStandardFieldLabel(i, e.target.value)}
                disabled={!field.enabled}
                style={{
                  padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.borderLight}`,
                  fontSize: 12, outline: 'none', background: field.enabled ? colors.bgCard : 'transparent',
                }}
              />

              <span style={{ fontSize: 11, color: colors.textMuted, textAlign: 'center' }}>{field.key}</span>

              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={() => toggleStandardField(i, 'required')}
                  disabled={!field.enabled}
                  style={{ accentColor: colors.slate }}
                />
                Pflicht
              </label>
            </div>
          ))}
        </div>
      </Card>

      {/* Custom Fields */}
      <Card title="Benutzerdefinierte Felder">
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 12px' }}>
          Zusätzliche Felder, die nur für deinen Tenant gelten.
        </p>

        {customFields.map((field, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 70px 28px', gap: 8,
            alignItems: 'center', marginBottom: 8,
          }}>
            <input
              value={field.label}
              onChange={e => updateCustomField(i, { label: e.target.value })}
              placeholder="Feldbezeichnung"
              style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, outline: 'none' }}
            />
            <select
              value={field.type}
              onChange={e => updateCustomField(i, { type: e.target.value as CustomFieldConfig['type'] })}
              style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 11 }}
            >
              <option value="text">Text</option>
              <option value="textarea">Textfeld</option>
              <option value="number">Zahl</option>
              <option value="select">Auswahl</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={field.required}
                onChange={() => updateCustomField(i, { required: !field.required })}
                style={{ accentColor: colors.slate }}
              />
              Pflicht
            </label>
            <button
              onClick={() => removeCustomField(i)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger, fontSize: 13, padding: 0 }}
              title="Feld entfernen"
            >
              <FaTimes />
            </button>
          </div>
        ))}

        <button
          onClick={addCustomField}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8, border: `1px dashed ${colors.border}`,
            background: 'transparent', cursor: 'pointer', fontSize: 12, color: colors.slate,
            fontWeight: 500, marginTop: 4,
          }}
        >
          <FaPlus style={{ fontSize: 10 }} /> Feld hinzufügen
        </button>
      </Card>

      {/* Email Templates */}
      <Card title="E-Mail Vorlagen">
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 12px' }}>
          Automatische E-Mails an Bewerber. Platzhalter: {'{company_name}'} und {'{first_name}'}.
        </p>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Reply-To E-Mail (fuer alle E-Mails)</label>
          <input
            value={emailReplyTo}
            onChange={e => setEmailReplyTo(e.target.value)}
            style={inputStyle}
            type="email"
            placeholder="z.B. bewerbung@katchi-ice.com"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Logo (fuer alle E-Mails)</label>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
          />
          {emailLogoUrl ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 12, background: colors.bgMuted, borderRadius: 10,
            }}>
              <img src={emailLogoUrl} alt="Email Logo" style={{ maxHeight: 48, maxWidth: 180 }} />
              <div style={{ flex: 1 }} />
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '5px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                  background: colors.bgCard, cursor: 'pointer', fontSize: 11, color: colors.textBody,
                }}
              >
                <FaUpload style={{ fontSize: 10 }} /> Ersetzen
              </button>
              <button
                onClick={handleLogoDelete}
                disabled={uploadingLogo}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '5px 10px', borderRadius: 6, border: `1px solid ${colors.danger}`,
                  background: 'transparent', cursor: 'pointer', fontSize: 11, color: colors.danger,
                }}
              >
                <FaTrash style={{ fontSize: 10 }} /> Entfernen
              </button>
            </div>
          ) : (
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 8, border: `1px dashed ${colors.border}`,
                background: 'transparent', cursor: 'pointer', fontSize: 12, color: colors.slate,
                fontWeight: 500, width: '100%', justifyContent: 'center',
              }}
            >
              <FaUpload style={{ fontSize: 12 }} />
              {uploadingLogo ? 'Wird hochgeladen...' : 'Logo hochladen (PNG, JPG, max. 2MB)'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {emailTemplates.map(tmpl => {
            const isExpanded = expandedTemplate === tmpl.step;
            return (
              <div
                key={tmpl.step}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: tmpl.is_active ? colors.bgCard : colors.bgMuted,
                  opacity: tmpl.is_active ? 1 : 0.65,
                }}
              >
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', cursor: 'pointer',
                  }}
                  onClick={() => setExpandedTemplate(isExpanded ? null : tmpl.step)}
                >
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      updateEmailTemplate(tmpl.step, { is_active: !tmpl.is_active });
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: 0 }}
                    title={tmpl.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {tmpl.is_active
                      ? <FaToggleOn color={colors.success} />
                      : <FaToggleOff color={colors.textMuted} />
                    }
                  </button>

                  <FaEnvelope style={{ fontSize: 12, color: colors.textMuted }} />

                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                    {tmpl.label}
                  </span>

                  {isExpanded
                    ? <FaChevronDown style={{ fontSize: 10, color: colors.textMuted }} />
                    : <FaChevronRight style={{ fontSize: 10, color: colors.textMuted }} />
                  }
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${colors.borderLight}` }}>
                    <div style={{ marginTop: 12, marginBottom: 10 }}>
                      <label style={labelStyle}>Betreff</label>
                      <input
                        value={tmpl.subject}
                        onChange={e => updateEmailTemplate(tmpl.step, { subject: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Text</label>
                      <textarea
                        value={tmpl.body}
                        onChange={e => updateEmailTemplate(tmpl.step, { body: e.target.value })}
                        rows={4}
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(135deg, #5271A5, #3b5998)', color: 'white',
            cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          <FaSave style={{ fontSize: 12 }} /> {saving ? 'Speichern...' : 'Konfiguration speichern'}
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{
      background: colors.bgCard, borderRadius: 16, border: `1px solid ${colors.border}`,
      boxShadow: shadows.card, padding: '20px 24px',
    }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
        {title}
      </h3>
      {children}
    </div>
  );
}
